import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { CheckCircle2, Download, ShieldCheck, ArrowLeft, PlayCircle, Copy, Check } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Extract a YouTube video id from various URL formats
const ytId = (url) => {
  if (!url) return "";
  const m = url.match(
    /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/
  );
  return m ? m[1] : "";
};

const track = (event, props = {}) => {
  try {
    if (typeof window !== "undefined" && window.posthog) window.posthog.capture(event, props);
  } catch (e) {}
};

const emptyBilling = {
  nome: "",
  cognome: "",
  email: "",
  codice_fiscale: "",
  partita_iva: "",
  via: "",
  citta: "",
  cap: "",
};

const Commerce = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [clientId, setClientId] = useState("");
  const [billing, setBilling] = useState(emptyBilling);
  const [touched, setTouched] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | paid | error
  const [downloadToken, setDownloadToken] = useState("");
  const [captureInfo, setCaptureInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    axios.get(`${API}/commerce/products`).then((r) => {
      setProduct(r.data.find((p) => p.id === id) || null);
    });
    axios.get(`${API}/commerce/config`).then((r) => setClientId(r.data.client_id));
    track("commerce_view", { product: id });
  }, [id]);

  const set = (k) => (e) => setBilling({ ...billing, [k]: e.target.value });

  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billing.email.trim());
  const hasFiscal =
    billing.codice_fiscale.trim() !== "" || billing.partita_iva.trim() !== "";
  const formValid =
    billing.nome.trim() &&
    billing.cognome.trim() &&
    emailOk &&
    billing.via.trim() &&
    billing.citta.trim() &&
    billing.cap.trim() &&
    hasFiscal;

  const triggerDownload = (token) => {
    const url = `${API}/commerce/download/${token}`;
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const fields = [
    { k: "nome", label: "Nome", req: true },
    { k: "cognome", label: "Cognome", req: true },
    { k: "email", label: "Email", req: true, type: "email" },
    { k: "codice_fiscale", label: "Codice Fiscale", req: false },
    { k: "partita_iva", label: "Partita IVA", req: false },
    { k: "via", label: "Indirizzo (via e n. civico)", req: true, full: true },
    { k: "citta", label: "Città", req: true },
    { k: "cap", label: "CAP", req: true },
  ];

  const isFieldError = (f) => {
    if (!touched) return false;
    if (f.k === "email") return !emailOk;
    if (f.k === "codice_fiscale" || f.k === "partita_iva") return !hasFiscal;
    if (f.req) return !billing[f.k].trim();
    return false;
  };

  if (product === null) {
    return (
      <div className="App">
        <Header />
        <div className="commerce-loading">Caricamento prodotto...</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App commerce-page">
      <Header />

      <div className="commerce-wrap">
        <Link to="/prodotti" className="commerce-back">
          <ArrowLeft size={16} strokeWidth={1.8} /> Torna agli strumenti
        </Link>

        {ytId(product.youtube_url) && (product.preview_mode !== "image" || !product.image) ? (
          <div className="commerce-video">
            <iframe
              src={`https://www.youtube.com/embed/${ytId(product.youtube_url)}`}
              title={product.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        ) : (
          product.image && (
            <img
              src={product.image.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${product.image}` : product.image}
              alt={product.title}
              className="commerce-img"
              data-testid="commerce-preview-image"
            />
          )
        )}

        <div className="commerce-head">
          <p className="section-eyebrow">{product.subtitle}</p>
          <h1 className="commerce-title serif">{product.title}</h1>
          <p className="commerce-price">
            € {Number(product.price).toFixed(2).replace(".", ",")}
          </p>
        </div>

        <p className="commerce-desc">{product.description}</p>

        {status === "paid" ? (
          <div className="commerce-success">
            <CheckCircle2 size={48} strokeWidth={1.3} className="success-icon" />
            <h2 className="serif">Grazie per l'acquisto!</h2>
            {captureInfo?.has_video ? (
              <>
                <p>
                  Il tuo corso è pronto. Apri il video dal pulsante qui sotto.
                  Ti abbiamo inviato lo stesso link anche via email.
                </p>
                <div className="success-link-box">
                  <input
                    readOnly
                    value={`${window.location.origin}/guarda/${downloadToken}`}
                    onFocus={(e) => e.target.select()}
                  />
                  <button
                    className="success-copy"
                    onClick={() => {
                      navigator.clipboard
                        ?.writeText(`${window.location.origin}/guarda/${downloadToken}`)
                        .then(() => {
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        });
                    }}
                  >
                    {copied ? <Check size={16} strokeWidth={1.8} /> : <Copy size={16} strokeWidth={1.8} />}
                    {copied ? "Copiato" : "Copia"}
                  </button>
                </div>
                <a
                  className="quiz-cta quiz-cta-primary"
                  href={`/guarda/${downloadToken}`}
                >
                  <PlayCircle size={16} strokeWidth={1.8} />
                  <span>Guarda il corso</span>
                </a>
              </>
            ) : (
              <>
                <p>
                  Il download è iniziato automaticamente. Ti abbiamo inviato
                  anche una email con il link.
                </p>
                <button
                  className="quiz-cta quiz-cta-primary"
                  onClick={() => triggerDownload(downloadToken)}
                >
                  <Download size={16} strokeWidth={1.8} />
                  <span>Scarica di nuovo</span>
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="commerce-checkout">
            <h2 className="commerce-form-title serif">Dati per la fatturazione</h2>
            <p className="commerce-form-sub">
              Tutti i campi contrassegnati con * sono obbligatori. Indica il
              Codice Fiscale oppure la Partita IVA.
            </p>

            <div className="billing-grid">
              {fields.map((f) => (
                <div
                  className={`billing-field ${f.full ? "full" : ""} ${
                    isFieldError(f) ? "err" : ""
                  }`}
                  key={f.k}
                >
                  <label>
                    {f.label}
                    {f.req && <span className="req">*</span>}
                  </label>
                  <input
                    type={f.type || "text"}
                    value={billing[f.k]}
                    onChange={set(f.k)}
                    onBlur={() => setTouched(true)}
                  />
                </div>
              ))}
            </div>
            {touched && !hasFiscal && (
              <p className="commerce-inline-err">
                Inserisci almeno il Codice Fiscale o la Partita IVA.
              </p>
            )}

            {errorMsg && <p className="admin-error">{errorMsg}</p>}

            <div className="paypal-zone">
              {!formValid && (
                <p className="paypal-note">
                  Compila i dati di fatturazione per abilitare il pagamento.
                </p>
              )}
              {clientId && (
                <PayPalScriptProvider
                  options={{ clientId, currency: "EUR", intent: "capture" }}
                >
                  <div style={{ opacity: formValid ? 1 : 0.5 }}>
                    <PayPalButtons
                      forceReRender={[formValid, product.id]}
                      disabled={!formValid}
                      style={{ layout: "vertical", color: "gold", shape: "rect", label: "paypal" }}
                      onClick={(data, actions) => {
                        setTouched(true);
                        if (!formValid) return actions.reject();
                        return actions.resolve();
                      }}
                      createOrder={async () => {
                        setErrorMsg("");
                        const res = await axios.post(`${API}/commerce/create-order`, {
                          product_id: product.id,
                          billing,
                        });
                        track("commerce_checkout_start", { product: product.id });
                        return res.data.paypal_order_id;
                      }}
                      onApprove={async (data) => {
                        try {
                          const res = await axios.post(`${API}/commerce/capture-order`, {
                            order_id: data.orderID,
                            origin: window.location.origin,
                          });
                          setDownloadToken(res.data.download_token);
                          setCaptureInfo(res.data);
                          setStatus("paid");
                          track("commerce_purchase", { product: product.id });
                          if (res.data.has_file && !res.data.has_video) {
                            triggerDownload(res.data.download_token);
                          }
                        } catch (e) {
                          setErrorMsg("Pagamento non riuscito. Riprova.");
                        }
                      }}
                      onError={() => setErrorMsg("Si è verificato un errore con PayPal. Riprova.")}
                    />
                  </div>
                </PayPalScriptProvider>
              )}
              <p className="paypal-secure">
                <ShieldCheck size={14} strokeWidth={1.8} /> Pagamento sicuro tramite PayPal
              </p>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Commerce;
